import { DropZone, Button } from "@shopify/polaris";
import { useState, useCallback } from "react";
import { postData } from "../../utilities/networkCalls";
import { DeleteIcon } from "@shopify/polaris-icons";
import Spinner from "../Icons/Spinner";
import config from "../../config";
import { ProductObj } from "../../apps/Bundles/Types";

type ImgProps = {
    handleInputChange:
        | ((index: number, field: any, value: string | number | boolean | ProductObj[]) => void)
        | ((field: any, value: string | number | boolean | ProductObj[]) => void);

    img_url: string;
    selectedTab: any;
    keyName: string;
    heading?: string;
    helpMessage?: string;
    styles?: object;
};

const ImageUploader = ({
    handleInputChange,
    selectedTab,
    img_url,
    keyName,
    heading,
    helpMessage,
    styles,
}: ImgProps) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const handleDropZoneDrop = useCallback(
        async (_dropFiles: any, acceptedFiles: any) => {
            const uploadedFile = acceptedFiles[0];

            if (!uploadedFile) return;

            setError("");
            setLoading(true);

            try {
                const uploadTo = async () => {
                    const formData = new FormData();
                    formData.append("file", uploadedFile);

                    const response = await postData({
                        url: `/${config.appName}/upload`,
                        payload: formData,
                    });

                    if (!response?.data?.url) {
                        if (response.status === 413) {
                            setError("File size is too big.");
                            return "";
                        }

                        throw new Error(`Image upload  failed`);
                    }

                    return response.data.url;
                };

                const [shopifyUrl] = await Promise.all([uploadTo()]);
                if (selectedTab != null) {
                    handleInputChange(selectedTab, keyName, shopifyUrl);
                } else {
                    handleInputChange(keyName, shopifyUrl);
                }
            } catch (err: any) {
                console.error("Upload error", err);
                setError("Failed to upload file");
            } finally {
                setLoading(false);
            }
        },
        [handleInputChange, keyName, selectedTab],
    );

    const handleRemoveFile = () => {
        if (selectedTab != null) {
            handleInputChange(selectedTab, keyName, "");
        } else {
            handleInputChange(keyName, "");
        }

        setError("");
    };

    const validTypes: any = {
        IMAGE: ["image/jpeg", "image/png", "image/jpg"],
    };

    const acceptedMimeTypes = validTypes["IMAGE"] || [];

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                gap: "7px",
                width: img_url?.length ? "100%" : "85%",
                margin: "auto",
            }}
        >
            {heading && heading?.length > 0 && (
                <p
                    style={{
                        fontSize: "12px",
                    }}
                >
                    {heading}
                </p>
            )}

            {img_url?.length == 0 ? (
                <DropZone
                    allowMultiple={false}
                    onDrop={handleDropZoneDrop}
                    accept={acceptedMimeTypes}
                    type="file"
                    disabled={loading}
                >
                    <DropZone.FileUpload
                        //@ts-expect-error doesnt support element type
                        actionTitle={loading ? <Spinner size={15} color="#303030" /> : `Upload image`}
                        //@ts-expect-error doesnt support element type
                        actionHint={
                            <span>
                                Drop file or click to upload (Max Size: 1MB). <br />
                                Accepts .jpg, .png, and .jpeg files.{" "}
                                {helpMessage && (
                                    <>
                                        <br />
                                        {helpMessage}
                                    </>
                                )}
                            </span>
                        }
                    />
                </DropZone>
            ) : (
                ""
            )}

            {img_url?.length > 0 && (
                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                    <img
                        src={img_url}
                        alt="Uploaded media"
                        style={{
                            width: "80%",
                            maxHeight: "200px",
                            ...styles,
                        }}
                    />
                    <div style={{ marginTop: 8 }}>
                        <Button
                            onClick={handleRemoveFile}
                            icon={DeleteIcon}
                            variant="secondary"
                            tone="critical"
                            accessibilityLabel="Delete"
                        ></Button>
                    </div>
                </div>
            )}

            {error && <p style={{ color: "red" }}>{error}</p>}
        </div>
    );
};

export default ImageUploader;
